import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useFieldElementRules } from "@/react/components/FormRenderer/hooks/useFieldElementRules";
import type { FieldElement } from "@/react/types/Element";
import { Form } from "@circle-react-uikit/Form";
import { t } from "@/i18n-js/instance";

interface DropdownElementProps {
  element: FieldElement;
  errors: Record<number, string>;
  fieldIndex: number;
}

export const DropdownElement = ({
  element,
  fieldIndex,
  errors,
}: DropdownElementProps) => {
  const { setError, setValue } = useFormContext();
  const idName = `elements[${fieldIndex}][id]`;
  const valueName = `elements[${fieldIndex}][value]`;
  const rules = useFieldElementRules(element);

  // Set initial value to empty string so the prompt is shown and no Option is selected by default
  useEffect(() => {
    setValue(valueName, "", { shouldDirty: false });
  }, [valueName, setValue]);

  useEffect(() => {
    if (errors && errors[fieldIndex]) {
      setError(`elements[${fieldIndex}][value]`, {
        type: "server",
        message: errors[fieldIndex],
      });
    }
  }, [errors, fieldIndex, setError]);

  const options = (element.attributes.options || []).map(option => ({
    label: option,
    value: option,
  }));

  return (
    <>
      <Form.Item
        label={element.attributes.label}
        name={valueName}
        rules={rules}
        hideDescription
        hideBorder
        forceRequiredLabel={element.attributes.required}
        labelClassName="!mb-4 [overflow-wrap:anywhere]"
      >
        <Form.SelectNative
          name={valueName}
          options={options}
          prompt={t("settings.forms.edit.defaults.dropdown_prompt")}
        />
      </Form.Item>
      <Form.Input
        wrapperClassName="hidden"
        type="hidden"
        name={idName}
        value={element.id}
      />
    </>
  );
};
